import { useRef, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Input, PageHeader, Pill, Table, Td, Th } from "@/components/ui";
import { popup, confirmPopup } from "@/components/popup";

interface Sub {
  id: number;
  user_id: number;
  username: string | null;
  kind: string;
  plan: string;
  endpoint_id: number | null;
  endpoint_name: string | null;
  endpoint_address: string | null;
  backend: string;
  device_limit: number;
  is_active: boolean;
  activated_at: string;
  expires_at: string | null;
  secret_preview: string | null;
  link: string | null;
}

interface VpnSub {
  telegram_id: number | null;
  username: string;
  status: string | null;
  expires_at: string | null;
  devices: number | null;
  used_traffic: number | null;
  short_uuid: string | null;
  user_id: number | null;
  local_username: string | null;
}

function fmt(s: string | null): string {
  if (!s) return "—";
  const d = new Date(s);
  return isNaN(+d)
    ? s
    : d.toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
}
function fmtBytes(n: number | null): string {
  if (!n) return "0";
  const u = ["B", "KB", "MB", "GB", "TB"];
  let i = 0, v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}

const PLANS = ["", "paid", "trial", "free_ad"];
const PLAN_LABEL: Record<string, string> = { "": "Все планы", paid: "paid", trial: "trial", free_ad: "ad" };

export default function Subscriptions() {
  const [tab, setTab] = useState<"proxy" | "vpn">("proxy");
  const counts = useQuery<{ total_proxy: number; total_vpn: number }>({
    queryKey: ["sub-counts"],
    queryFn: () => api("/api/admin/subscriptions?status="),
    refetchInterval: 30000,
  });
  // VPN живёт в Remnawave (локальный total_vpn всегда 0). Счётчик берём оттуда же,
  // что и список (общий queryKey — react-query дедуплицирует запрос).
  const vpnCount = useQuery<{ available: boolean; subscriptions: unknown[] }>({
    queryKey: ["vpn-subscriptions"],
    queryFn: () => api("/api/admin/vpn/subscriptions"),
    refetchInterval: 30000,
  });
  const vpnN = vpnCount.data?.available ? vpnCount.data.subscriptions.length : null;

  return (
    <div className="p-4 md:p-8">
      <PageHeader title="Подписки" />
      <div className="flex items-center gap-2 mb-5">
        <TabBtn active={tab === "proxy"} onClick={() => setTab("proxy")}>
          Proxy <span className="opacity-60">{counts.data?.total_proxy ?? ""}</span>
        </TabBtn>
        <TabBtn active={tab === "vpn"} onClick={() => setTab("vpn")}>
          VPN <span className="opacity-60">{vpnN ?? ""}</span>
        </TabBtn>
      </div>
      {tab === "proxy" ? <ProxyTab /> : <VpnTab />}
    </div>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`h-8 px-4 rounded-lg text-sm font-medium border transition-all ${
        active ? "bg-brand-soft text-brand border-brand/40 glow-ring" : "bg-black/20 border-white/[0.08] text-zinc-400 hover:text-zinc-200"
      }`}
    >
      {children}
    </button>
  );
}

function ProxyTab() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [plan, setPlan] = useState("");
  const [status, setStatus] = useState("active");

  const params = new URLSearchParams({ kind: "proxy" });
  if (search.trim()) params.set("search", search.trim());
  if (plan) params.set("plan", plan);
  if (status) params.set("status", status);

  const q = useQuery<{ subscriptions: Sub[]; total: number }>({
    queryKey: ["subscriptions", "proxy", search.trim(), plan, status],
    queryFn: () => api(`/api/admin/subscriptions?${params.toString()}`),
    refetchInterval: 20000,
  });
  const subs = q.data?.subscriptions ?? [];
  const refresh = () => qc.invalidateQueries({ queryKey: ["subscriptions"] });

  return (
    <>
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        <Input
          placeholder="Поиск: user_id, telegram_id или @username"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full sm:w-80"
        />
        <div className="flex items-center gap-1">
          {PLANS.map((p) => (
            <FilterChip key={p || "all"} active={plan === p} onClick={() => setPlan(p)}>
              {PLAN_LABEL[p]}
            </FilterChip>
          ))}
        </div>
        <div className="flex items-center gap-1">
          {[["active", "Активные"], ["", "Все"], ["inactive", "Выкл"]].map(([k, l]) => (
            <FilterChip key={k || "all"} active={status === k} onClick={() => setStatus(k)}>
              {l}
            </FilterChip>
          ))}
        </div>
        <div className="ml-auto text-zinc-500 text-sm">найдено: {subs.length}</div>
      </div>

      {q.isLoading && <div className="text-zinc-400">Загрузка…</div>}
      {!q.isLoading && subs.length === 0 && <div className="text-zinc-500">Ничего не найдено.</div>}

      {subs.length > 0 && (
        <Table>
          <thead>
            <tr>
              <Th fit>#</Th>
              <Th>Юзер</Th>
              <Th>План</Th>
              <Th>Хост</Th>
              <Th align="right" fit>Устройств</Th>
              <Th>Секрет</Th>
              <Th>Статус</Th>
              <Th align="right">Истекает</Th>
              <Th fit>{" "}</Th>
            </tr>
          </thead>
          <tbody>
            {subs.map((s) => (
              <ProxyRow key={s.id} s={s} onChanged={refresh} />
            ))}
          </tbody>
        </Table>
      )}
    </>
  );
}

function ProxyRow({ s, onChanged }: { s: Sub; onChanged: () => void }) {
  const [menu, setMenu] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!menu) return;
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setMenu(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [menu]);

  const act = (path: string, method = "POST", body?: unknown) =>
    api(`/api/admin/subscriptions/${s.id}${path}`, { method, body: body ? JSON.stringify(body) : undefined });

  const extend = useMutation({
    mutationFn: () => act("/extend", "POST", { days: 30 }),
    onSuccess: () => { popup({ tone: "success", title: "Продлено +30 дней", body: "Юзеру ушло уведомление." }); onChanged(); },
    onError: (e) => popup({ tone: "error", title: "Продление", body: (e as Error).message }),
  });
  const rotate = useMutation({
    mutationFn: () => act("/rotate"),
    onSuccess: () => { popup({ tone: "success", title: "Секрет сменён", body: "Старая ссылка мертва, юзеру ушла новая." }); onChanged(); },
    onError: (e) => popup({ tone: "error", title: "Ротация", body: (e as Error).message }),
  });
  const deactivate = useMutation({
    mutationFn: () => act("/deactivate"),
    onSuccess: () => { popup({ tone: "success", title: "Деактивирована" }); onChanged(); },
    onError: (e) => popup({ tone: "error", title: "Деактивация", body: (e as Error).message }),
  });
  const del = useMutation({
    mutationFn: () => act("", "DELETE"),
    onSuccess: () => { popup({ tone: "success", title: "Удалена" }); onChanged(); },
    onError: (e) => popup({ tone: "error", title: "Удаление", body: (e as Error).message }),
  });

  const busy = extend.isPending || rotate.isPending || deactivate.isPending || del.isPending;

  return (
    <tr className="hover:bg-white/[0.02] transition-colors">
      <Td fit><span className="font-mono text-xs text-zinc-500">{s.id}</span></Td>
      <Td>
        <Link to={`/users/${s.user_id}`} className="text-brand hover:underline text-sm">
          {s.username ? `@${s.username}` : `#${s.user_id}`}
        </Link>
      </Td>
      <Td><Pill tone={s.plan === "paid" ? "success" : "neutral"}>{s.plan}</Pill></Td>
      <Td>
        {s.endpoint_id ? (
          <Link to="/endpoints" className="text-zinc-300 hover:text-brand text-sm" title={s.endpoint_address ?? ""}>
            {s.endpoint_name ?? `ep#${s.endpoint_id}`}
          </Link>
        ) : <span className="text-zinc-600 text-sm">—</span>}
      </Td>
      <Td align="right" fit><span className="font-mono text-sm text-zinc-300">{s.device_limit}</span></Td>
      <Td><span className="font-mono text-xs text-zinc-500">{s.secret_preview ? `${s.secret_preview}…` : "—"}</span></Td>
      <Td><Pill tone={s.is_active ? "success" : "danger"}>{s.is_active ? "активна" : "выкл"}</Pill></Td>
      <Td align="right"><span className="text-xs text-zinc-400 tabular-nums">{fmt(s.expires_at)}</span></Td>
      <Td fit>
        <div className="relative" ref={ref}>
          <button
            onClick={() => setMenu((v) => !v)}
            disabled={busy}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-zinc-500 hover:text-zinc-100 hover:bg-white/[0.06] transition-colors"
          >
            {busy ? "…" : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <circle cx="12" cy="5" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="12" cy="19" r="1.6" />
              </svg>
            )}
          </button>
          {menu && (
            <div className="absolute right-0 top-8 z-20 min-w-[180px] rounded-xl card-elev shadow-pop py-1 text-sm">
              {s.link && (
                <MenuItem onClick={() => { setMenu(false); navigator.clipboard?.writeText(s.link!); popup({ tone: "success", title: "Ссылка скопирована" }); }}>
                  Скопировать ссылку
                </MenuItem>
              )}
              <MenuItem onClick={() => { setMenu(false); extend.mutate(); }}>Продлить +30 дней</MenuItem>
              <MenuItem onClick={async () => { setMenu(false); if (await confirmPopup({ title: "Сменить секрет?", body: "Старая ссылка юзера перестанет работать, придёт новая.", confirmLabel: "Сменить" })) rotate.mutate(); }}>
                Ротация секрета
              </MenuItem>
              {s.is_active && (
                <MenuItem onClick={async () => { setMenu(false); if (await confirmPopup({ title: "Деактивировать?", body: "Секрет снимется с выхода.", danger: true, confirmLabel: "Деактивировать" })) deactivate.mutate(); }}>
                  Деактивировать
                </MenuItem>
              )}
              <div className="my-1 border-t border-white/[0.06]" />
              <MenuItem danger onClick={async () => { setMenu(false); if (await confirmPopup({ title: "Удалить подписку?", body: `#${s.id} — необратимо.`, danger: true, confirmLabel: "Удалить" })) del.mutate(); }}>
                Удалить
              </MenuItem>
            </div>
          )}
        </div>
      </Td>
    </tr>
  );
}

function VpnTab() {
  const q = useQuery<{ available: boolean; reason?: string; subscriptions: VpnSub[] }>({
    queryKey: ["vpn-subscriptions"],
    queryFn: () => api("/api/admin/vpn/subscriptions"),
    refetchInterval: 30000,
  });
  const d = q.data;

  if (q.isLoading) return <div className="text-zinc-400">Загрузка…</div>;
  if (!d?.available)
    return (
      <div className="rounded-xl border border-amber-500/20 bg-amber-500/5 p-6 text-sm text-amber-300/90 max-w-2xl">
        VPN-подписки живут в Remnawave (истина по VPN — там). {d?.reason ? `Сейчас недоступно: ${d.reason}.` : ""}
        <div className="text-zinc-500 mt-2">На проде здесь появятся все VPN-юзера с их статусом, сроком и устройствами.</div>
      </div>
    );
  const subs = d.subscriptions ?? [];
  if (subs.length === 0) return <div className="text-zinc-500">VPN-подписок нет.</div>;

  return (
    <Table>
      <thead>
        <tr>
          <Th>Юзер</Th>
          <Th>Статус</Th>
          <Th align="right" fit>Устройств</Th>
          <Th align="right">Трафик</Th>
          <Th align="right">Истекает</Th>
        </tr>
      </thead>
      <tbody>
        {subs.map((v, i) => (
          <tr key={i} className="hover:bg-white/[0.02] transition-colors">
            <Td>
              {v.user_id ? (
                <Link to={`/users/${v.user_id}`} className="text-brand hover:underline text-sm">
                  {v.local_username ? `@${v.local_username}` : `#${v.user_id}`}
                </Link>
              ) : (
                <span className="text-zinc-400 text-sm font-mono">{v.username || (v.telegram_id ? `tg${v.telegram_id}` : "—")}</span>
              )}
            </Td>
            <Td><Pill tone={v.status === "ACTIVE" ? "success" : "danger"}>{v.status ?? "—"}</Pill></Td>
            <Td align="right" fit><span className="font-mono text-sm text-zinc-300">{v.devices ?? "—"}</span></Td>
            <Td align="right"><span className="text-xs text-zinc-400">{fmtBytes(v.used_traffic)}</span></Td>
            <Td align="right"><span className="text-xs text-zinc-400 tabular-nums">{fmt(v.expires_at)}</span></Td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

function FilterChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`h-8 px-3 rounded-lg text-xs border transition-all ${
        active ? "bg-brand-soft text-brand border-brand/40" : "bg-black/20 border-white/[0.08] text-zinc-400 hover:text-zinc-200"
      }`}
    >
      {children}
    </button>
  );
}

function MenuItem({ children, onClick, danger = false }: { children: React.ReactNode; onClick: () => void; danger?: boolean }) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-3.5 py-2 transition-colors ${danger ? "text-rose-400 hover:bg-rose-500/10" : "text-zinc-300 hover:bg-white/[0.05]"}`}
    >
      {children}
    </button>
  );
}
